import { Grid, useMediaQuery } from "@mui/material";
import Layout from "../../../components/Layout";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import BreadCrumb from "../../../components/BreadCrumb";
import { Icon } from "@iconify/react";
import Button from "../../../components/Button";
import { useEffect, useState } from "react";
import InputGroup from "../../../components/FormComponent/InputGroup";
import Modal from "../../../components/Modal";
import useStyles from "../style";
import { useNavigate, useParams } from "react-router-dom";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";

const SubscriberDetails = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({ name: "", email: "" });
  const { id } = useParams();
  const [data, setData] = useState({});
  const [subscriberData, setSubscriberData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editSubscriber, setEditSubscriber] = useState({ name: "", email: "" });
  const [currentRow, setCurrentRow] = useState({});
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);

  const fetchSubscriberData = () => {
    httprequest
      .get(`/api/v1/email-sub/${id}`)
      .then((res) => {
        setData(res.data.data);
        setSubscriberData(res.data.data.members);
        setFilteredSubscribers(res.data.data.members);
      })
      .catch(() => {
      });
  };

  useEffect(() => {
    fetchSubscriberData();
  }, []);

  /**
   *  @function toggleEditModal
   *  @description This function toggles the edit modal for a subscriber.
   * It sets the current row and initializes the editSubscriber state with the row's data.
   * @param {*} row
   */
  const toggleEditModal = (row) => {
    if (row) {
      setCurrentRow(row);
      setEditSubscriber({ name: row.name, email: row.email });
    }
    setCurrentRow(row);
    setIsEditModalOpen(!isEditModalOpen);
  };

  const toggleDeleteModal = (row) => {
    setCurrentRow(row);
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Name</p>}
          {row.name}
        </>
      )
    },
    {
      name: "Email",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Email</p>}
          {row.email}
        </>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button
            className="table-action-icon"
            onClick={() => toggleEditModal(row)}
          >
            <Icon icon="lucide:edit" />
          </button>
          <button className="table-action-icon"
            onClick={() => toggleDeleteModal(row)}
          >
            <div className="delete-icon"><Icon icon="mingcute:delete-2-line" /></div>
          </button>
        </div>
      )
    }
  ];
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditSubscriber({ ...editSubscriber, [name]: value });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSubscriber({ ...newSubscriber, [name]: value });
  };

  /**
   * @function handleAddSubscriber
   * @description This function handles the addition of a new subscriber.
   * It sends a POST request to the API with the new subscriber data.
   */
  const handleAddSubscriber = () => {
    setIsLoading(true);
    const memberData = {
      members: [newSubscriber]
    };

    httprequest
      .post(`/api/v1/email-sub/${id}/member`, memberData)
      .then((res) => {
        toast.success(res.data.message);
        setNewSubscriber({ name: "", email: "" });
        fetchSubscriberData();
        toggleModal();
      })
      .catch((error) => {
        toast.error(error.data.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * @function handleEditSubscriber
   *  @description This function handles the editing of a subscriber.
   * It sends a PATCH request to the API with the updated subscriber data.
   */
  const handleEditSubscriber = () => {
    setIsLoading(true);
    httprequest
      .patch(`/api/v1/email-sub/member/${currentRow?.id}`, editSubscriber)
      .then((res) => {
        toast.success(res.data.message);
        fetchSubscriberData();
        toggleEditModal();
      })
      .catch((error) => {
        toast.error(error.data.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * @function handleDeleteSubscriber
   * @description This function handles the deletion of a subscriber.
   * It sends a DELETE request to the API with the subscriber's ID.
   */
  const handleDeleteSubscriber = () => {
    setIsLoading(true);
    httprequest
      .delete(`/api/v1/email-sub/member/${currentRow?.id}`)
      .then((res) => {
        toast.success(res.data.message);
        fetchSubscriberData();
        toggleDeleteModal();
      })
      .catch((error) => {
        toast.error(error.data.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchSubscribers = (e) => {
    const { value } = e.target;
    if (!value) {
      setFilteredSubscribers(subscriberData);

      return;
    }
    const searchValue = value.toLowerCase();
    const valArray = searchValue.split(" ");
    const result = subscriberData.filter((subscriber) => valArray.every(
      (word) => (subscriber?.name && subscriber.name.toLowerCase().includes(word)) ||
          (subscriber?.email && subscriber.email.toLowerCase().includes(word))
    )
    );
    setFilteredSubscribers(result);
  };

  return (
    <Layout children={
      <>
        <BreadCrumb
          link_one="/messaging/subcriber"
          link_one_name="Subscriber"
          active={data.name}
          description="You can manage your emails and subscription here."
        />
        <div className="page-content margin-top-30">
          <div className="action_div">
            <p style={{ fontWeight: 500, fontSize: "14px" }}>{data.name}</p>
            <div className="flex gap-10">
              <Button
                variant="primaryoutline"
                buttonSize="fluid"
                color="btnfontprimary"
                buttonName="Add Member"
                onClick={toggleModal}
              />
              <Button
                variant="primary"
                buttonSize="fluid"
                color="btndefault"
                buttonName="Send Email to List"
                onClick={() => navigate("/messaging/sendemails")}
              />
            </div>
          </div>
          <Table
            data={filteredSubscribers}
            columns={columns}
            pagination
            subHeader={true}
            subHeaderComponent={
              <TableSearch
                placeholder="Search subscribers..."
                searchTableFunc={handleSearchSubscribers}
              />
            }
          />
        </div>
        <Modal
          title="Add Member"
          subtitle="Add a new member to a subscriber list"
          modalContent={
            <Grid container spacing={2} className="input-padding">
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  inputType="text"
                  label="Name"
                  inputName="name"
                  value={newSubscriber.name}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  inputType="email"
                  label="Email"
                  inputName="email"
                  value={newSubscriber.email}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          }
          modalFooter={
            <div className={classes.modalFooterBtn}>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Add Member"
                isLoading={isLoading}
                onClick={handleAddSubscriber}
              />
            </div>
          }
          isopen={isModalOpen}
          closeModal={toggleModal}
        />
        <Modal
          title="Edit Member"
          subtitle="Edit the subscriber's details"
          modalContent={
            <Grid container spacing={2} className="input-padding">
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  inputType="text"
                  label="Name"
                  inputName="name"
                  value={editSubscriber.name}
                  onChange={handleEditInputChange}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  inputType="email"
                  label="Email"
                  inputName="email"
                  value={editSubscriber.email}
                  onChange={handleEditInputChange}
                />
              </Grid>
            </Grid>
          }
          modalFooter={
            <div className={classes.modalFooterBtn}>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Update Member"
                isLoading={isLoading}
                onClick={handleEditSubscriber}
              />
            </div>
          }
          isopen={isEditModalOpen}
          closeModal={toggleEditModal}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          title="Delete Member"
          subtitle={`Are you sure you want to delete ${editSubscriber.name}? This action cannot be undone.`}
          modalContent={<></>}
          modalFooter={
            <div className={classes.modalFooterBtn}>
              <Button
                variant="danger"
                buttonSize="full"
                color="btndefault"
                buttonName="Delete Member"
                isLoading={isLoading}
                onClick={handleDeleteSubscriber}
              />
            </div>
          }
          isopen={isDeleteModalOpen}
          closeModal={toggleDeleteModal}
        />
        {/* <ToastContainer /> */}

      </>
    }
    />
  );
};

export default SubscriberDetails;
